import {
  useQuery,
  UseQueryOptions,
  QueryClient,
} from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { ApiClient } from 'services/ApiClient'
import { Api } from 'types/Api'
import { ShoppingCart } from 'models/Product'
import { useAuth } from 'contexts/AuthContext'
import { ApplyDiscountRequest } from 'models/Discount'
import { useEffect } from 'react'

export namespace FetchShoppingCart {
  export type Response = ShoppingCart
  export type Variables = {
    clientId?: string
    discount?: ApplyDiscountRequest
  }
  export type Error = Api.Error
  export type Options = UseQueryOptions<Response, Error>
}

const createKey = (data: FetchShoppingCart.Variables) => ['shoppingCart', { data }]

const queryFetcher = (data: FetchShoppingCart.Variables) => () => {
  if (!data.clientId) return Promise.reject()
  return ApiClient.post<FetchShoppingCart.Response>(`/v1/shopping-cart/preview`, { data })
}

export const useFetchShoppingCart = (
  data: Omit<FetchShoppingCart.Variables, 'clientId'>,
  options?: FetchShoppingCart.Options
) => {
  const { clientId, confirmed } = useAuth()

  return useQuery<FetchShoppingCart.Response, FetchShoppingCart.Error>(
    createKey({ ...data, clientId }),
    queryFetcher({ ...data, clientId }),
    {
      ...options,
      enabled: confirmed,
    }
  )
}

export const prefetchShoppingCart = async (
  clientId: string,
  options?: FetchShoppingCart.Options
) => {
  const queryClient = new QueryClient()

  await queryClient.prefetchQuery<FetchShoppingCart.Response, FetchShoppingCart.Error>(
    createKey({ clientId }),
    queryFetcher({ clientId }),
    options
  )

  return {
    dehydratedState: dehydrate(queryClient),
  }
}
